import React, { useState, useMemo, useEffect } from 'react';
import Header from '../../components/ui/Header';
import QuickAddMenu from '../../components/ui/QuickAddMenu';
import Button from '../../components/ui/Button';
import SEO from '../../components/SEO';
import Icon from '../../components/AppIcon';
import PermitFilters from './components/PermitFilters';
import PermitTable from './components/PermitTable';
import PermitDetails from './components/PermitDetails';
import CalendarView from './components/CalendarView';
import AddPermitModal from './components/AddPermitModal';
import PermissionDenied from '../../components/ui/PermissionDenied';
import { usePermissions } from '../../hooks/usePermissions';
import { permitService } from '../../services/permitService';
import { supabase } from '../../lib/supabase';
import { exportToCSV, PERMIT_CSV_COLUMNS } from '../../utils/exportToCSV';

const Permits = () => {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [showAddModal, setShowAddModal] = useState(false);
  const [permDeniedMsg, setPermDeniedMsg] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    permitType: 'all',
    status: 'all',
    deadline: 'all'
  });
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPermits();

    const handleOpenPermitDetails = async (event) => {
      const { permitId } = event?.detail || {};
      if (permitId) {
        try {
          const permit = await permitService?.getById(permitId);
          if (permit) {
            setSelectedPermit(permit);
          }
        } catch (error) {
          console.error('Error loading permit:', error);
        }
      }
    };

    window.addEventListener('openPermitDetails', handleOpenPermitDetails);

    return () => {
      window.removeEventListener('openPermitDetails', handleOpenPermitDetails);
    };
  }, []);

  // Real-time subscriptions for permits and permit status changes
  useEffect(() => {
    const channel = supabase
      ?.channel('permits_realtime')
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'permits' },
        (payload) => {
          console.log('Permit change detected:', payload);
          loadPermits();
          // If the changed permit is currently selected, refresh it
          if (payload?.new?.id && selectedPermit?.id === payload?.new?.id) {
            setSelectedPermit(prev => prev ? { ...prev, ...payload?.new } : prev);
          }
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [selectedPermit?.id]);

  const loadPermits = async () => {
    setLoading(true);
    try {
      const data = await permitService?.getAll();
      setPermits(data || []);
    } catch (error) {
      console.error('Error loading permits:', error);
      setPermits([]);
    } finally {
      setLoading(false);
    }
  };

  const showPermDenied = (msg) => {
    setPermDeniedMsg(msg);
    setTimeout(() => setPermDeniedMsg(''), 4000);
  };

  const handleNewApplication = () => {
    if (!canCreate) {
      showPermDenied('You have viewer access only. Contact an admin to add permit applications.');
      return;
    }
    setShowAddModal(true);
  };

  const handlePermitSuccess = () => {
    loadPermits();
    setShowAddModal(false);
  };

  const handlePermitUpdate = async () => {
    await loadPermits();
    if (selectedPermit?.id) {
      try {
        const updated = await permitService?.getById(selectedPermit?.id);
        if (updated) setSelectedPermit(updated);
      } catch (err) {
        console.error('Error refreshing selected permit:', err);
      }
    }
  };

  const filteredPermits = useMemo(() => {
    return permits?.filter(permit => {
      const matchesSearch = !filters?.search || 
        permit?.projectName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        permit?.applicationNumber?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      
      const matchesType = filters?.permitType === 'all' || permit?.permitType === filters?.permitType;
      const matchesStatus = filters?.status === 'all' || permit?.status === filters?.status;
      
      let matchesDeadline = true;
      if (filters?.deadline !== 'all' && permit?.deadline) {
        const today = new Date();
        const deadline = new Date(permit?.deadline);
        const daysUntilDeadline = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
        
        if (filters?.deadline === 'overdue') {
          matchesDeadline = daysUntilDeadline < 0;
        } else if (filters?.deadline === 'week') {
          matchesDeadline = daysUntilDeadline >= 0 && daysUntilDeadline <= 7;
        } else if (filters?.deadline === 'month') {
          matchesDeadline = daysUntilDeadline >= 0 && daysUntilDeadline <= 30;
        }
      }
      
      return matchesSearch && matchesType && matchesStatus && matchesDeadline;
    });
  }, [permits, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      permitType: 'all',
      status: 'all',
      deadline: 'all'
    });
  };

  const statusCounts = useMemo(() => {
    return {
      total: permits?.length,
      submission: permits?.filter(p => p?.status === 'submission')?.length,
      underReview: permits?.filter(p => p?.status === 'under review')?.length,
      approved: permits?.filter(p => p?.status === 'approved')?.length,
      rejected: permits?.filter(p => p?.status === 'rejected')?.length
    };
  }, [permits]);

  const handleExportCSV = () => {
    const exportData = filteredPermits?.map(p => ({
      ...p,
      submissionDate: p?.submissionDate ? new Date(p.submissionDate)?.toLocaleDateString() : '',
      deadline: p?.deadline ? new Date(p.deadline)?.toLocaleDateString() : ''
    }));
    exportToCSV(
      exportData,
      PERMIT_CSV_COLUMNS,
      `permits_export_${new Date()?.toISOString()?.slice(0, 10)}`
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Permits & Compliance — ADU Permit Tracking Software"
        description="Track ADU permit applications, deadlines, and compliance documents. Never miss a permit deadline with ADU Flow's automated permit management and calendar integration."
        canonical="/permits"
        keywords="ADU permits, permit tracking, compliance management, permit deadlines, permit applications"
        noIndex={true}
      />
      <Header />
      <QuickAddMenu />
      
      <main className="container mx-auto px-4 pt-20 pb-6 md:pt-24 md:pb-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                Permits & Compliance
              </h1>
              <p className="text-sm text-muted-foreground">
                Manage permit applications, track status, and ensure regulatory compliance
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1">
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  iconName="List"
                  onClick={() => setViewMode('table')}
                >
                  List
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                  size="sm"
                  iconName="Calendar"
                  onClick={() => setViewMode('calendar')}
                >
                  Calendar
                </Button>
              </div>
              {canCreate && (
                <Button iconName="Plus" iconPosition="left" onClick={handleNewApplication} className="h-10">
                  New Application
                </Button>
              )}
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={handleExportCSV}
                disabled={!filteredPermits?.length}
                className="h-10"
              >
                Export
              </Button>
            </div>
          </div>
        </div>

        {permDeniedMsg && (
          <PermissionDenied message={permDeniedMsg} className="mb-4" />
        )}

        {/* Status Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6">
          <div className="bg-card rounded-lg p-3 sm:p-4 border border-border">
            <div className="flex items-center gap-2 mb-1.5">
              <Icon name="FileText" size={16} className="text-primary" />
              <span className="text-xs sm:text-sm text-muted-foreground">Total</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-foreground">{statusCounts?.total}</div>
          </div>
          <div className="bg-card rounded-lg p-3 sm:p-4 border border-border">
            <div className="flex items-center gap-2 mb-1.5">
              <Icon name="Clock" size={16} className="text-blue-500" />
              <span className="text-xs sm:text-sm text-muted-foreground">Submission</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-foreground">{statusCounts?.submission}</div>
          </div>
          <div className="bg-card rounded-lg p-3 sm:p-4 border border-border">
            <div className="flex items-center gap-2 mb-1.5">
              <Icon name="Search" size={16} className="text-warning" />
              <span className="text-xs sm:text-sm text-muted-foreground">Under Review</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-foreground">{statusCounts?.underReview}</div>
          </div>
          <div className="bg-card rounded-lg p-3 sm:p-4 border border-border">
            <div className="flex items-center gap-2 mb-1.5">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-xs sm:text-sm text-muted-foreground">Approved</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-foreground">{statusCounts?.approved}</div>
          </div>
          <div className="bg-card rounded-lg p-3 sm:p-4 border border-border">
            <div className="flex items-center gap-2 mb-1.5">
              <Icon name="XCircle" size={16} className="text-destructive" />
              <span className="text-xs sm:text-sm text-muted-foreground">Rejected</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-foreground">{statusCounts?.rejected}</div>
          </div>
        </div>

        {/* Filters */}
        <PermitFilters 
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className={selectedPermit ? "lg:col-span-2" : "lg:col-span-3"}>
            {viewMode === 'table' ? (
              <PermitTable 
                permits={filteredPermits}
                selectedPermit={selectedPermit}
                onSelectPermit={setSelectedPermit}
              />
            ) : (
              <CalendarView 
                permits={filteredPermits}
                onSelectPermit={setSelectedPermit}
              />
            )}
          </div>
          
          {selectedPermit && (
            <div className="hidden lg:block lg:col-span-1">
              <PermitDetails 
                permit={selectedPermit}
                onClose={() => setSelectedPermit(null)}
                onUpdate={handlePermitUpdate}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            </div>
          )}
        </div>

        {/* Mobile: PermitDetails as bottom sheet */}
        {selectedPermit && (
          <div className="lg:hidden">
            <div
              className="fixed inset-0 z-[350] bg-black/50"
              onClick={() => setSelectedPermit(null)}
            />
            <div
              className="fixed bottom-0 left-0 right-0 z-[351] bg-card rounded-t-2xl shadow-2xl"
              style={{ maxHeight: '88vh', paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))' }}
            >
              <div className="w-10 h-1 bg-border rounded-full mx-auto mt-3 mb-1 flex-shrink-0" />
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(88vh - 24px)' }}>
                <PermitDetails 
                  permit={selectedPermit}
                  onClose={() => setSelectedPermit(null)}
                  onUpdate={handlePermitUpdate}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              </div>
            </div>
          </div>
        )}
      </main>

      <AddPermitModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handlePermitSuccess}
      />
    </div>
  );
};

export default Permits;